import { useState, useEffect } from 'react';
import {
  Globe, Shield, ShieldCheck, Copy, Download, ExternalLink,
  Loader2, QrCode, Eye, EyeOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTranslation } from '@/i18n/TranslationContext';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getScoreColor(score) {
  if (score >= 80) return 'text-vrs-green';
  if (score >= 60) return 'text-vrs-amber';
  return 'text-vrs-red';
}

function getProgressBg(score) {
  if (score >= 80) return '[&>div]:bg-vrs-green';
  if (score >= 60) return '[&>div]:bg-vrs-amber';
  return '[&>div]:bg-vrs-red';
}

export default function PublicScoresPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/public/profile/me');
      setProfile(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchProfile(); }, []);

  const handleToggle = async (enabled) => {
    setToggling(true);
    try {
      await api.post('/public/profile/toggle', { is_public: enabled });
      toast.success(enabled ? 'Public profile enabled!' : 'Public profile disabled');
      await fetchProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
    setToggling(false);
  };

  const copyUrl = () => {
    if (profile?.public_url) {
      navigator.clipboard.writeText(profile.public_url);
      toast.success('Public URL copied to clipboard');
    }
  };

  const downloadQr = (format) => {
    if (user?.id) {
      window.open(`${BACKEND_URL}/api/public/profile/qr/${user.id}?format=${format}`, '_blank');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const isPublic = profile?.is_public || false;
  const score = profile?.overall_score || 0;
  const categories = profile?.category_scores || [];

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="public-scores-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">{t('public_compliance_profile')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('public_profile_desc')}</p>
      </div>

      {/* Toggle */}
      <Card className="border-border/60">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isPublic ? <Eye className="h-5 w-5 text-vrs-green" /> : <EyeOff className="h-5 w-5 text-muted-foreground" />}
              <div>
                <p className="text-sm font-semibold text-foreground">{t('make_score_public')}</p>
                <p className="text-xs text-muted-foreground">When enabled, anyone with the link or QR code can view your overall score and category breakdown.</p>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={handleToggle}
              disabled={toggling}
              data-testid="public-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {isPublic && (
        <>
          {/* QR Code + URL */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <QrCode className="h-5 w-5 text-primary" />Your QR Code
              </CardTitle>
              <CardDescription>Share this QR code with vendors, partners, or clients. Anyone who scans it can view your public compliance score.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                {/* QR Image */}
                <div className="flex-shrink-0 bg-white rounded-lg p-3 border border-border/60">
                  <img
                    src={`${BACKEND_URL}/api/public/profile/qr/${user?.id}?format=png`}
                    alt="QR Code"
                    className="w-48 h-48"
                    data-testid="qr-code-image"
                  />
                </div>
                {/* Actions */}
                <div className="flex-1 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">{t('public_url')}</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-muted/30 rounded-lg border border-border px-3 py-2 font-mono truncate" data-testid="public-url">
                        {profile?.public_url}
                      </code>
                      <Button variant="outline" size="sm" onClick={copyUrl} data-testid="copy-url-button">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => downloadQr('png')} data-testid="download-qr-png">
                      <Download className="mr-2 h-4 w-4" />Download PNG
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadQr('svg')} data-testid="download-qr-svg">
                      <Download className="mr-2 h-4 w-4" />Download SVG
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(profile?.public_url, '_blank')} data-testid="preview-profile">
                      <ExternalLink className="mr-2 h-4 w-4" />Preview
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Score Preview */}
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">{t('profile_preview')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center space-y-2">
                <p className={cn('text-5xl font-bold font-mono tabular-nums', getScoreColor(score))}>{score}%</p>
                <p className="text-sm text-muted-foreground">Overall Compliance Score</p>
              </div>
              {categories.length > 0 && (
                <div className="space-y-2">
                  {categories.map((cat, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-foreground w-32 truncate">{cat.category}</span>
                      <div className="flex-1">
                        <Progress value={cat.score} className={cn('h-1.5', getProgressBg(cat.score))} />
                      </div>
                      <span className={cn('text-xs font-bold font-mono w-10 text-right', getScoreColor(cat.score))}>{cat.score}%</span>
                    </div>
                  ))}
                </div>
              )}
              {categories.length === 0 && (
                <p className="text-center text-sm text-muted-foreground">No scan results yet. Run a scan to populate your public profile.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
